import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Card, Descriptions, InputNumber, Modal, Space, Tag, Typography } from 'antd';
import { ClearOutlined, EyeOutlined } from '@ant-design/icons';
import { get, post } from '../../api/client';

const { Text } = Typography;

/** 与 backend/src/modules/uploads/orphan-file.service.ts 的 SweepReport 逐字对齐 */
interface SweepReport {
  root: string;
  ttlDays: number;
  dryRun: boolean;
  scanned: number;
  referenced: number;
  keptYoung: number;
  deleted: number;
  wouldDelete: number;
  failed: string[];
  paths: string[];
}

const ENDPOINT = '/admin/uploads/orphan-files';

export default function FileCleanupPage() {
  const { message } = App.useApp();
  const [ttlDays, setTtlDays] = useState<number | null>(null);
  const [report, setReport] = useState<SweepReport | null>(null);

  const preview = useMutation({
    mutationFn: () => get<SweepReport>(ENDPOINT, ttlDays ? { ttlDays } : undefined),
    onSuccess: (data) => setReport(data),
    onError: (e) => message.error(e instanceof Error ? e.message : '预览失败'),
  });

  const sweep = useMutation({
    mutationFn: () => post<SweepReport>(`${ENDPOINT}/sweep`, ttlDays ? { ttlDays } : {}),
    onSuccess: (data) => {
      setReport(data);
      // 结论读 data：unwrapResponse 有 data 时会丢掉 message
      message.success(`已清理 ${data.deleted} 个文件${data.failed.length ? `，${data.failed.length} 个删不掉` : ''}`);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '清理执行失败'),
  });

  const askSweep = () =>
    Modal.confirm({
      title: `确认删除这 ${report?.wouldDelete ?? 0} 个文件？`,
      content:
        '删除不可恢复。这些文件已经满 TTL 且全库没有任何一行指向它们，但「没有引用」判据来自当前库快照——若你刚上传了还没提交的凭证，请先把 TTL 调大再执行。',
      okText: '确认清理',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => sweep.mutate(),
    });

  return (
    <Card title="孤儿上传文件清理">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="这里只处理「上传后没有提交」的文件"
        description="在用文件由业务写入口当场回收（换图即删旧值），本页面只负责清理没有任何一行数据引用的落盘文件：满保留天数且全库零引用才删，其余一律保留。系统每天 04:00 已自动执行一次，这里的按钮用于上线首遍核对与临时调整天数。"
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Text>保留天数</Text>
        <InputNumber
          min={1}
          precision={0}
          style={{ width: 120 }}
          placeholder="默认 7"
          value={ttlDays}
          onChange={(v) => setTtlDays(typeof v === 'number' ? v : null)}
        />
        <Button icon={<EyeOutlined />} loading={preview.isPending} onClick={() => preview.mutate()}>
          预览待清理名单
        </Button>
        <Button
          danger
          icon={<ClearOutlined />}
          disabled={!report || report.wouldDelete === 0 || !report.dryRun}
          onClick={askSweep}
        >
          执行清理
        </Button>
      </Space>

      {!report && !preview.isPending && !sweep.isPending ? (
        <Text type="secondary">还没有取过报告。先点「预览待清理名单」看清将被删除的文件，再决定是否执行。</Text>
      ) : null}

      {report && (
        <Card size="small" title={report.dryRun ? '预览结果（未删除任何文件）' : '清理结果'}>
          <Descriptions size="small" column={2} style={{ marginBottom: 8 }}>
            <Descriptions.Item label="判定口径">
              满 {report.ttlDays} 天且全库零引用
            </Descriptions.Item>
            <Descriptions.Item label="扫描目录">{report.root}</Descriptions.Item>
            <Descriptions.Item label="扫描文件数">{report.scanned}</Descriptions.Item>
            <Descriptions.Item label="仍被引用">{report.referenced}</Descriptions.Item>
            <Descriptions.Item label="在途保留（未到天数）">{report.keptYoung}</Descriptions.Item>
            <Descriptions.Item label={report.dryRun ? '待删除' : '已删除'}>
              <Text type={report.dryRun ? 'warning' : 'danger'}>
                {report.dryRun ? report.wouldDelete : report.deleted}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          {report.failed.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              title={`${report.failed.length} 个文件没能删掉`}
              description={report.failed.join('、')}
            />
          )}

          <Space size={4} wrap style={{ marginBottom: 8 }}>
            <Text type="secondary">{report.dryRun ? '将被删除' : '已删除'}（最多列出 200 条）：</Text>
          </Space>
          {report.paths.length === 0 ? (
            <Tag>无</Tag>
          ) : (
            <div style={{ maxHeight: 260, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
              {report.paths.map((p) => (
                <div key={p}>{p}</div>
              ))}
            </div>
          )}
        </Card>
      )}
    </Card>
  );
}
